// import { init as signup } from 'components/signup';
import Toast from "components/toast"
import PhotosState from "./photos-state"

/** @typedef {import('../repository/comment-votes-repository').default} CommentVotesRepository */
/** @typedef {import('../repository/photos-repository').default} PhotosRepository */
/** @typedef {import('../repository/user-repository').default} UserRepository */
/** @typedef {import('../models/comment-entity').default} CommentEntity */
/** @typedef {import('../models/photo-entity').default} PhotoEntity */

/* eslint-disable no-underscore-dangle */
export default class PhotosBloc {
  /**
   * Creates an instance of PhotosBloc.
   * @param {Object} settings
   * @param {function(PhotosState) : void} settings.build
   * @param {PhotoEntity} settings.currentPhoto
   * @param {UserRepository} settings.userRepository
   * @param {PhotosRepository} settings.photosRepository
   * @param {CommentVotesRepository} settings.commentVotesRepository
   * @memberof PhotosBloc
   */
  constructor({ build, currentPhoto, userRepository, photosRepository, commentVotesRepository }) {
    this._build = build
    this._userRepository = userRepository
    this._photosRepository = photosRepository
    this._commentVotesRepository = commentVotesRepository

    this.state = PhotosState.initial().copyWith({
      backwardPhoto: photosRepository.loadFromStorage({
        photoId: currentPhoto.id,
        direction: "backward",
      }),
      currentPhoto,
      forwardPhoto: photosRepository.loadFromStorage({
        photoId: currentPhoto.id,
        direction: "forward",
      }),
    })
  }

  photosPreloaded() {
    this.update(
      this.state.copyWith({
        backwardPhoto: this._photosRepository.loadFromStorage({
          photoId: this.state.currentPhoto.id,
          direction: "backward",
        }),
        forwardPhoto: this._photosRepository.loadFromStorage({
          photoId: this.state.currentPhoto.id,
          direction: "forward",
        }),
      }),
    )
  }

  /**
   * @param {String} direction
   * @memberof PhotosBloc
   */
  async photosLoadRequested(direction) {
    this.update(this.state.copyWith({ loading: true }))

    try {
      const newPhoto = await this._photosRepository.load({
        photoId: this.state.currentPhoto.id,
        direction,
      })

      if (newPhoto === null) {
        if (direction === "backward") {
          this.update(this.state.copyWith({ backwardPhoto: null, loading: false }))
        }
        if (direction === "forward") {
          this.update(this.state.copyWith({ forwardPhoto: null, loading: false }))
        }
      } else {
        await this._photosRepository.preload(newPhoto.id)
        this.update(
          this.state.copyWith({
            backwardPhoto: this._photosRepository.loadFromStorage({
              photoId: newPhoto.id,
              direction: "backward",
            }),
            currentPhoto: newPhoto,
            forwardPhoto: this._photosRepository.loadFromStorage({
              photoId: newPhoto.id,
              direction: "forward",
            }),
          }),
        )
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      this.update(this.state.copyWith({ error: true }))
    }
  }

  photosLoaded() {
    this.update(
      this.state.copyWith({
        loading: false,
      }),
    )
  }

  photosButtonsVisibilityUpdated() {
    this.update(
      this.state.copyWith({
        backwardPhoto:
          this.state.backwardPhoto === null
            ? this._photosRepository.loadFromStorage({
                photoId: this.state.currentPhoto.id,
                direction: "backward",
              })
            : null,
        forwardPhoto:
          this.state.forwardPhoto === null
            ? this._photosRepository.loadFromStorage({
                photoId: this.state.currentPhoto.id,
                direction: "forward",
              })
            : null,
      }),
    )
  }

  /**
   * @param {Number} commentId
   * @memberof PhotosBloc
   */
  async photosCommentUpvoted(commentId) {
    if (this._userRepository.isGuest) {
      // signup();
      return
    }

    const { comments } = this.state.currentPhoto
    const comment = comments.find(({ id }) => id === commentId)
    const { votes } = comment

    let newVotes
    if (votes.isUpvotedByUser) {
      newVotes = votes.copyWith({
        upvotes: votes.upvotes - 1,
        isUpvotedByUser: false,
      })
    } else {
      newVotes = votes.copyWith({
        upvotes: votes.upvotes + 1,
        isUpvotedByUser: true,
      })
    }

    if (votes.isDownvotedByUser) {
      newVotes = newVotes.copyWith({
        downvotes: newVotes.downvotes - 1,
        isDownvotedByUser: false,
      })
    }

    const newComment = comment.copyWith({
      votes: newVotes,
    })

    // Optimistic UI immediately shows the result
    this.update(
      this.state.copyWith({
        currentPhoto: this.state.currentPhoto.copyWith({
          comments: this._replaceCommentInListById(this.state.currentPhoto.comments, newComment),
        }),
      }),
    )

    let result
    if (votes.isUpvotedByUser) {
      result = await this._commentVotesRepository.undoUpvote(commentId)
    } else {
      result = await this._commentVotesRepository.voteUp(commentId)
    }

    if (result.error === null) {
      this._photosRepository.updatePhotoInStorage(this.state.currentPhoto)
    } else {
      this.update(
        this.state.copyWith({
          currentPhoto: this.state.currentPhoto.copyWith({
            comments,
          }),
        }),
      )
    }
  }

  /**
   * @param {Number} commentId
   * @memberof PhotosBloc
   */
  async photosCommentDownvoted(commentId) {
    if (this._userRepository.isGuest) {
      // signup();
      return
    }

    const { comments } = this.state.currentPhoto
    const comment = comments.find(({ id }) => id === commentId)
    const { votes } = comment

    let newVotes
    if (votes.isDownvotedByUser) {
      newVotes = votes.copyWith({
        downvotes: votes.downvotes - 1,
        isDownvotedByUser: false,
      })
    } else {
      newVotes = votes.copyWith({
        downvotes: votes.downvotes + 1,
        isDownvotedByUser: true,
      })
    }

    if (votes.isUpvotedByUser) {
      newVotes = newVotes.copyWith({
        upvotes: newVotes.upvotes - 1,
        isUpvotedByUser: false,
      })
    }

    const newComment = comment.copyWith({
      votes: newVotes,
    })

    // Optimistic UI immediately shows the result
    this.update(
      this.state.copyWith({
        currentPhoto: this.state.currentPhoto.copyWith({
          comments: this._replaceCommentInListById(this.state.currentPhoto.comments, newComment),
        }),
      }),
    )

    let result
    if (votes.isDownvotedByUser) {
      result = await this._commentVotesRepository.undoDownvote(commentId)
    } else {
      result = await this._commentVotesRepository.voteDown(commentId)
    }

    if (result.error === null) {
      this._photosRepository.updatePhotoInStorage(this.state.currentPhoto)
    } else {
      this.update(
        this.state.copyWith({
          currentPhoto: this.state.currentPhoto.copyWith({
            comments,
          }),
        }),
      )
    }
  }

  /**
   * @param {Number} rating
   * @returns
   * @memberof PhotosBloc
   */
  async photosVoted(rating) {
    if (this._userRepository.isGuest) {
      // signup();
      return
    }

    const toast = Toast.generator({
      duration: 5000,
      gravity: "bottom",
      position: "center",
      offset: { x: 0, y: 10 },
    })

    if (rating === null) {
      toast({
        text: "Please, choose the desired rating",
        color: "white",
        backgroundColor: "orange",
      }).build()

      return
    }

    const result = await this._photosRepository.vote(this.state.currentPhoto.id, rating)

    if (result.success === true) {
      toast({
        text: "Thank you for voting!",
        color: "black",
        backgroundColor: "white",
      }).build()
    } else {
      toast({
        text: result.error.toString(),
        color: "white",
        backgroundColor: "red",
      }).build()
    }
  }

  /**
   * @param {CommentEntity[]} commentList
   * @param {CommentEntity} newComment
   * @memberof PhotosBloc
   */
  // eslint-disable-next-line class-methods-use-this
  _replaceCommentInListById(commentList, newComment) {
    const index = commentList.findIndex(({ id }) => id === newComment.id)

    const newCommentList = []
    newCommentList.push(...commentList.slice(0, index))
    newCommentList.push(newComment)
    newCommentList.push(...commentList.slice(index + 1))

    return newCommentList
  }

  /**
   * @param {PhotosState} newState
   * @memberof PhotosBloc
   */
  update(newState) {
    // console.log(this.state, newState);
    // console.log('equals', this.state.equals(newState));

    if (this.state.equals(newState)) return
    this._build(newState)
    this.state = newState
  }
}
